import { normalizeSelectionRange, rangeToUtf16, type TextSelectionRange } from '@/utils/text-kit';
import { TextWritingType, type ITextContent, type ITextLayerProps } from '@/version-types';
import { applyArtTextAttrsToRange, createTextContentsByArtText } from './art-text';
import { segmentGraphemes } from './base';

type TextContentAttrPatch<K extends keyof ITextContent = keyof ITextContent> = {
    key: K;
    value: ITextContent[K];
};

type TextRangeAttrs = Pick<
    ITextContent,
    'color' | 'fontFamily' | 'fontSize' | 'bold' | 'italic' | 'underline' | 'lineThrough' | 'textMark'
>;

const SHARED_TEXT_CONTENT_ATTR_FIELDS = [
    'color',
    'fontFamily',
    'fontSize',
    'bold',
    'italic',
    'underline',
    'lineThrough',
    'textMark',
] as const satisfies readonly (keyof ITextContent)[];

const extractLayerSharedTextContentAttrs = (data: ITextLayerProps): TextRangeAttrs => ({
    color: data.color,
    fontFamily: data.fontFamily,
    fontSize: data.fontSize,
    bold: data.bold,
    italic: data.italic,
    underline: data.underline,
    lineThrough: data.lineThrough,
    textMark: data.textMark,
});

const textContentAttrsEqual = (a: Partial<TextRangeAttrs>, b: Partial<TextRangeAttrs>) => {
    return (
        JSON.stringify(Object.fromEntries(SHARED_TEXT_CONTENT_ATTR_FIELDS.map((field) => [field, a[field]]))) ===
        JSON.stringify(Object.fromEntries(SHARED_TEXT_CONTENT_ATTR_FIELDS.map((field) => [field, b[field]])))
    );
};

/**
 * 读取单个 textContent 的最终属性。
 *
 * textContent 只保存覆盖图层默认值的局部属性；这里需要先取图层级属性作为默认值，
 * 再用 textContent 上显式声明的属性覆盖，得到工具栏实际应该展示的片段属性。
 *
 * @param data - 当前文本图层数据。
 * @param textContent - 当前选区命中的文本片段。
 * @returns 合并图层默认值后的文本片段属性。
 */
const resolveTextContentAttrs = (data: ITextLayerProps, textContent: ITextContent): TextRangeAttrs => {
    const attrs = extractLayerSharedTextContentAttrs(data);
    SHARED_TEXT_CONTENT_ATTR_FIELDS.forEach((field) => {
        if (textContent[field] !== undefined) Reflect.set(attrs, field, textContent[field]);
    });
    return attrs;
};

const applyAttrPatch = (textContent: ITextContent, patch: Partial<ITextContent>) => {
    const next: ITextContent = { ...textContent };
    const keys = Object.keys(patch) as (keyof ITextContent)[];

    for (const key of keys) {
        if (key === 'text') continue;

        const value = patch[key];
        if (value === undefined) {
            Reflect.deleteProperty(next, key);
        } else {
            Reflect.set(next, key, value);
        }
    }

    return next;
};

/**
 * 计算新旧文本从开头起连续相同的 grapheme 长度，用作文本 diff 左边界。
 *
 * @param a - 旧文本。
 * @param b - 新文本。
 * @returns 公共前缀的 UTF-16 长度。
 */
const commonPrefixLen = (a: string, b: string) => {
    const aSegments = segmentGraphemes(a);
    const bSegments = segmentGraphemes(b);

    const max = Math.min(aSegments.length, bSegments.length);
    let index = 0;
    let length = 0;

    while (index < max && aSegments[index] === bSegments[index]) {
        length += aSegments[index].length;
        index++;
    }

    return length;
};

/**
 * 计算新旧文本从结尾起连续相同的 grapheme 长度，用作文本 diff 右边界。
 *
 * @param a - 旧文本。
 * @param b - 新文本。
 * @param prefix - 已确认的公共前缀长度，避免前后边界重叠。
 * @returns 公共后缀的 UTF-16 长度。
 */
const commonSuffixLen = (a: string, b: string, prefix: number) => {
    const aSegments = segmentGraphemes(a.slice(prefix));
    const bSegments = segmentGraphemes(b.slice(prefix));

    const max = Math.min(aSegments.length, bSegments.length);
    let index = 0;
    let length = 0;

    while (index < max && aSegments[aSegments.length - 1 - index] === bSegments[bSegments.length - 1 - index]) {
        length += aSegments[aSegments.length - 1 - index].length;
        index++;
    }

    return length;
};

const normalizeTextContents = (data: ITextLayerProps) => {
    const { textContents = [] } = data;

    if (textContents && textContents.length > 0) return textContents;
    if (!data.text) return undefined;

    const attrs = extractLayerSharedTextContentAttrs(data);
    const textContent: ITextContent = {
        text: data.text,
        ...attrs,
    };

    return [textContent];
};

const compactTextContents = (data: ITextLayerProps): ITextContent[] | undefined => {
    let textContents = data.textContents || [];
    textContents = textContents.filter((textContent) => textContent.text.length > 0);

    if (textContents.length === 0) return undefined;

    const merged: ITextContent[] = [];

    for (const cur of textContents) {
        const prev = merged[merged.length - 1];
        if (prev && textContentAttrsEqual(prev, cur)) {
            merged[merged.length - 1] = { ...prev, text: prev.text + cur.text };
        } else {
            merged.push(cur);
        }
    }

    if (merged.length === 1) {
        const textContent = merged[0];
        const attrs = extractLayerSharedTextContentAttrs(data);
        const fullMatched = SHARED_TEXT_CONTENT_ATTR_FIELDS.every(
            (field) =>
                textContent[field] === undefined || JSON.stringify(textContent[field]) === JSON.stringify(attrs[field]),
        );

        if (fullMatched) return undefined;
    }

    return merged;
};

/**
 * 根据文本变更同步维护 textContents，并尽量继承原分段样式。
 *
 * @param data - 变更前的文本图层数据。
 * @param prev - 变更前的完整文本。
 * @param next - 变更后的完整文本。
 * @returns 变更后的 textContents；返回 undefined 表示无需持久化分段数据。
 */
export const applyTextDiff = (data: ITextLayerProps, prev: string, next: string) => {
    if (data.advancedTextMaterial && data.writingMode === TextWritingType.HORIZONTAL_TB && !data.shapeExtension) {
        return createTextContentsByArtText(next, data);
    }

    const textContents = normalizeTextContents(data);
    if (!textContents || prev === next) return textContents;

    const prefixLen = commonPrefixLen(prev, next);
    const suffixLen = commonSuffixLen(prev, next, prefixLen);
    const changedStart = prefixLen;
    const changedEnd = prev.length - suffixLen;
    const insertText = next.slice(prefixLen, next.length - suffixLen);

    // cases:
    //  - 纯删除：insertText.length === 0
    //  - 纯插入：changedStart === changedEnd
    //  - 替换：changedStart < changedEnd && insertText.length > 0

    const ret: ITextContent[] = [];

    let cursor = 0;
    let inserted = false;

    for (const item of textContents) {
        const start = cursor;
        const end = cursor + item.text.length;
        cursor = end;

        const leftEnd = Math.min(end, changedStart);

        // 当前 item 与删除起点左侧有交集时，先保留这段未变文本
        // 覆盖场景：
        //  - 变更起点在当前 item 内，保留 item 左半段
        //  - 变更起点超出当前 item 右侧，保留整个 item
        if (leftEnd > start) {
            const preserved = prev.slice(start, leftEnd);
            ret.push({ ...item, text: preserved });
        }

        // 变更起点位于当前 item 内部或右边界时，插入新的 textContent 并继承当前 item 的样式
        // 覆盖场景：
        //  - 变更起点落在当前 item 内部：start < changedStart < end
        //  - 变更起点落在当前 item 右边界：changedStart === end
        // Q：为什么不是 >= start？
        // A：当 changedStart === start 时，应由前一个 item 处理
        if (!inserted && insertText.length > 0 && changedStart > start && changedStart <= end) {
            ret.push({ ...item, text: insertText });
            inserted = true;
        }

        const rightStart = Math.max(start, changedEnd);

        // 当前 item 与删除终点右侧有交集时，保留这段未变文本
        if (end > rightStart) {
            ret.push({ ...item, text: prev.slice(rightStart, end) });
        }
    }

    // 变更起点在首个 item 左边界时，循环内不会插入；此时插入文本放到开头并继承首段样式
    if (!inserted && insertText.length > 0) {
        ret.unshift({ ...textContents[0], text: insertText });
    }

    return compactTextContents({ ...data, text: next, textContents: ret });
};

/**
 * 读取 UTF-8 文本选区覆盖到的 textContent 属性列表。
 *
 * 光标态没有选中文本，需要归属到一个 textContent：全文开头归第一段，其它位置归光标左侧所在片段。
 * 选区态只要和 textContent 有交集，就把该片段属性加入列表，用于后续判断 uniform / mixed。
 *
 * @param data - 当前文本图层数据。
 * @param range - UTF-8 字节偏移表示的文本选区。
 * @returns 选区覆盖到的文本片段属性列表；返回 null 表示没有可读文本片段。
 */
const getTextRangeAttrsList = (data: ITextLayerProps, range: TextSelectionRange): TextRangeAttrs[] | null => {
    const textContents = normalizeTextContents(data);
    if (!textContents) return null;

    const [rangeStart, rangeEnd] = rangeToUtf16(data.text, normalizeSelectionRange(range));
    const matched: ITextContent[] = [];

    let cursor = 0;
    for (const item of textContents) {
        const start = cursor;
        const end = cursor + item.text.length;
        cursor = end;

        const collapsed = rangeStart === rangeEnd;
        const contained = rangeStart === 0 ? start === 0 : rangeStart > start && rangeStart <= end;
        const overlapped = rangeEnd > start && rangeStart < end;

        if ((collapsed && contained) || (!collapsed && overlapped)) matched.push(item);
    }

    if (matched.length === 0) return null;

    return matched.map((item) => resolveTextContentAttrs(data, item));
};

/**
 * 读取 UTF-8 文本选区命中的共享文本属性状态。
 *
 * none 表示没有命中文本；uniform 表示命中分段属性一致；mixed 表示至少一个共享属性不一致。
 *
 * @param data - 当前文本图层数据。
 * @param range - UTF-8 字节偏移表示的文本选区。
 * @returns 选区属性状态。
 */
export const resolveRangeAttrsState = (data: ITextLayerProps, range: TextSelectionRange) => {
    const attrsList = getTextRangeAttrsList(data, range);
    if (!attrsList) return { type: 'none' } as const;

    const attrs = attrsList[0];
    const mixedFields = SHARED_TEXT_CONTENT_ATTR_FIELDS.filter((field) => {
        return attrsList.some((item) => JSON.stringify(attrs[field]) !== JSON.stringify(item[field]));
    });

    if (mixedFields.length === 0) return { type: 'uniform', attrs } as const;

    const uniformAttrs = Object.fromEntries(
        SHARED_TEXT_CONTENT_ATTR_FIELDS.filter((field) => !mixedFields.includes(field)).map((field) => [
            field,
            attrs[field],
        ]),
    ) as Partial<TextRangeAttrs>;

    return { type: 'mixed', attrs: uniformAttrs, mixedFields } as const;
};

/**
 * 将属性补丁应用到 UTF-8 文本选区覆盖的 textContents。
 *
 * @param data - 当前文本图层数据。
 * @param range - UTF-8 字节偏移表示的文本选区。
 * @param attrs - 要写入选区 textContents 的属性补丁。
 * @returns 应用补丁并压缩后的 textContents；返回 undefined 表示无需持久化分段数据。
 */
const applyAttrsToRange = (data: ITextLayerProps, range: TextSelectionRange, attrs: Partial<ITextContent>) => {
    const sorted = normalizeSelectionRange(range);
    if (sorted.start === sorted.end) return data.textContents;
    if (data.advancedTextMaterial && data.writingMode === TextWritingType.HORIZONTAL_TB && !data.shapeExtension) {
        return applyArtTextAttrsToRange(data, sorted, attrs);
    }

    const textContents = normalizeTextContents(data);
    if (!textContents) return data.textContents;

    // ATTENTION: TextSelectionRange 使用 UTF-8 字节偏移；
    //            下面切分 textContent.text 时依赖 JS 字符串的 length/slice，
    //            它们使用 UTF-16 索引，所以必须先转换为 UTF-16 索引。
    const [rangeStart, rangeEnd] = rangeToUtf16(data.text, sorted);

    const ret: ITextContent[] = [];
    let cursor = 0;

    for (const item of textContents) {
        const start = cursor;
        const end = cursor + item.text.length;
        cursor = end;

        // 当前 item 与选区没有交集，原样保留。
        if (end <= rangeStart || start >= rangeEnd) {
            ret.push(item);
            continue;
        }

        // 当前 item 左侧超出选区的部分不应用属性补丁，先保留原样式。
        if (start < rangeStart) {
            const preserved = item.text.slice(0, rangeStart - start);
            ret.push({ ...item, text: preserved });
        }

        const middleStart = Math.max(start, rangeStart) - start;
        const middleEnd = Math.min(end, rangeEnd) - start;

        // 当前 item 与选区重叠的中间部分应用属性补丁。
        if (middleEnd > middleStart) {
            const newText = item.text.slice(middleStart, middleEnd);
            const newItem = applyAttrPatch({ ...item, text: newText }, attrs);
            ret.push(newItem);
        }

        // 当前 item 右侧超出选区的部分不应用属性补丁，保留原样式。
        if (end > rangeEnd) {
            const preserved = item.text.slice(rangeEnd - start);
            ret.push({ ...item, text: preserved });
        }
    }

    return compactTextContents({ ...data, textContents: ret });
};

/**
 * 将单个文本属性应用到 UTF-8 文本选区，并返回更新后的 textContents 与规范化选区。
 *
 * @param data - 当前文本图层数据。
 * @param range - UTF-8 字节偏移表示的文本选区。
 * @param patch - 要写入 textContents 的属性名和值。
 * @returns 更新后的 textContents 与规范化后的选区。
 */
export const applyTextRangeAttrs = (data: ITextLayerProps, range: TextSelectionRange, patch: TextContentAttrPatch) => {
    const textContents = applyAttrsToRange(data, range, {
        [patch.key]: patch.value,
    });

    return {
        textContents,
        range: normalizeSelectionRange(range),
    };
};
